import { SearchableModel } from "@/common/types/searchableModel.type";
import { ChevronRightIcon } from "lucide-react";
import React, { useEffect, useRef } from "react";
import { twMerge } from "tailwind-merge";

interface ListModelsProps {
  enabled: boolean;
  search: string;
  models: SearchableModel[];
  onSelect: (index: number) => void;
  selectedIndex: number;
  handleKeyboard: (
    onArrowDown: (index: number) => number,
    onSelect: (index: number) => void
  ) => (event: KeyboardEvent) => void;
  handleKeyScroll: (listElement: HTMLDivElement, selectedItemElement: HTMLDivElement) => void;
}

const ListModels = ({
  enabled,
  search,
  models,
  onSelect,
  selectedIndex,
  handleKeyboard,
  handleKeyScroll
}: ListModelsProps) => {
  const listRef = useRef<HTMLDivElement>(null);
  const selectedItemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKeyboardArrowDown = (index: number) => {
      if (index < models.length - 1) {
        return index + 1;
      }

      return index;
    }

    const onKeyboardSelect = (index: number) => {
      onSelect(index);
    }

    const handleKeydown = (event: KeyboardEvent) => handleKeyboard(onKeyboardArrowDown, onKeyboardSelect)(event);

    if (enabled) {
      document.removeEventListener('keydown', handleKeydown, true);
      document.addEventListener('keydown', handleKeydown, true);
    } else {
      document.removeEventListener('keydown', handleKeydown, true);
    }

    return () => document.removeEventListener('keydown', handleKeydown, true);
  }, [enabled, selectedIndex, search]);

  useEffect(() => {
    const listElement = listRef.current;
    const selectedItemElement = selectedItemRef.current;

    if (listElement && selectedItemElement) {
      handleKeyScroll(listElement, selectedItemElement);
    }
  }, [selectedIndex]);

  return (
    <div className="flex flex-col gap-2 overflow-auto h-[21rem]" ref={listRef}>
      {models.map((model, index) => (
        <div
          className={twMerge(
            'bg-white-100 flex items-center justify-between p-4 rounded-lg text-white-400 hover:bg-white-200 cursor-pointer',
            index === selectedIndex && 'border-2 border-white-400 bg-white-200'
          )}
          onClick={() => onSelect(index)}
          ref={index === selectedIndex ? selectedItemRef : null}
          key={`list_model_${index}`}
        >
          <div className="flex items-center gap-2">
            {React.createElement(model.icon, { strokeWidth: 1.5 })}

            <p>{model.label}</p>
          </div>

          <ChevronRightIcon strokeWidth={1.5} />
        </div>
      ))}
    </div>
  );
};

export default ListModels;
